import React, { createContext, useContext, useState, useEffect } from 'react';

export interface GlucoseReading {
  value: number;
  type: 'fasting' | 'postMeal';
  time: string;
  date: string;
}

export interface A1cReading {
  value: number;
  date: string;
}

interface GlucoseContextType {
  readings: GlucoseReading[];
  a1cReadings: A1cReading[];
  addReading: (reading: GlucoseReading) => void;
  addA1cReading: (reading: A1cReading) => void;
  getAverageGlucose: () => number;
  getTodayReadings: () => GlucoseReading[];
  getWeekReadings: () => GlucoseReading[];
}

const GlucoseContext = createContext<GlucoseContextType | undefined>(undefined);

export const GlucoseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [readings, setReadings] = useState<GlucoseReading[]>(() => {
    const saved = localStorage.getItem('glucoseReadings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return [];
      }
    }
    return [];
  });

  const [a1cReadings, setA1cReadings] = useState<A1cReading[]>(() => {
    const saved = localStorage.getItem('a1cReadings');
    if (saved) {
      try { return JSON.parse(saved); } catch { return []; }
    }
    return [];
  });

  useEffect(() => {
    localStorage.setItem('glucoseReadings', JSON.stringify(readings));
  }, [readings]);

  useEffect(() => {
    localStorage.setItem('a1cReadings', JSON.stringify(a1cReadings));
  }, [a1cReadings]);

  const addReading = (reading: GlucoseReading) => {
    setReadings(prev => [reading, ...prev]);
  };

  const addA1cReading = (reading: A1cReading) => {
    setA1cReadings(prev => [reading, ...prev].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
  };

  const getTodayReadings = () => {
    const today = new Date().toISOString().split('T')[0];
    return readings.filter(r => r.date === today);
  };

  const getWeekReadings = () => {
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return readings.filter(r => new Date(r.date) >= weekAgo);
  };

  const getAverageGlucose = () => {
    const todayReadings = getTodayReadings();
    if (todayReadings.length === 0) return 0;
    const sum = todayReadings.reduce((acc, r) => acc + r.value, 0);
    return Math.round(sum / todayReadings.length);
  };

  return (
    <GlucoseContext.Provider value={{ readings, a1cReadings, addReading, addA1cReading, getAverageGlucose, getTodayReadings, getWeekReadings }}>
      {children}
    </GlucoseContext.Provider>
  );
};

export const useGlucose = () => {
  const context = useContext(GlucoseContext);
  if (context === undefined) {
    throw new Error('useGlucose must be used within a GlucoseProvider');
  }
  return context;
};
